import React, { useMemo } from "react";

import useTimeState from "hooks/dashboard/useTimeState";
import { LocalDateTime, ZoneId } from "@js-joda/core";
import { useGetDataInfoRange, useGetRecentData } from "api/api-hooks/datasets-hooks";
import { ProgressSpinner } from "primereact/progressspinner";
import { T } from "services/lang/LanguageService";
import { isNumeric } from "utils/utils";
import SparkLineHandler from "./SparkLineHandler";
import { AdvancedProps, SparkLineProps, Usages } from "./SparkLineTypes";
import { defaultFromDateTime, defaultToDateTime } from "../charts/utils";

export default function SparkLine(props: SparkLineProps) {
  const valueScaling = props.valueScaling || 1;
  const count = props.count || 14;
  const dataset = isNumeric(props.dataset)
    ? `${props.dataset}:CATEGORIZED_DATASET`
    : (props.dataset as string);

  const timeState = useTimeState({
    from: props.from,
    to: props.to,
  });

  const defaultFromDate =
    timeState.from ??
    defaultFromDateTime({
      loadConf: {
        alignUnit: "MINUTES",
        lag: 6,
        lagUnit: "HOURS",
        lead: 0,
        leadUnit: "HOURS",
        ...props.loadConf,
      },
      referenceTime: props.referenceTime,
    });

  const defaultToDate =
    timeState.to ??
    defaultToDateTime({
      loadConf: {
        alignUnit: "MINUTES",
        lag: 6,
        lagUnit: "HOURS",
        lead: 0,
        leadUnit: "HOURS",
        ...props.loadConf,
      },
      referenceTime: props.referenceTime,
    });

  const { data, isLoading } = useGetRecentData([dataset], {
    count,
    refTime:
      props.referenceTime &&
      LocalDateTime.parse(props.referenceTime).atZone(ZoneId.systemDefault()).toString(),
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled: !!props.dataset && !props.useLoadConf,
    },
  });

  const { data: dataRange, isLoading: isLoadingRange } = useGetDataInfoRange(
    [dataset],
    {
      timeStart: defaultFromDate?.toISOString(),
      timeEnd: defaultToDate?.toISOString(),
    },
    {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled: !!props.dataset && props.useLoadConf && !!defaultFromDate && !!defaultToDate,
    }
  );

  const sortedData = useMemo(() => {
    if (props.useLoadConf) {
      return dataRange?.[0].data.sort((a, b) => a.key.localeCompare(b.key)) || [];
    }
    return data?.[0].data.sort((a, b) => a.key.localeCompare(b.key)) || [];
  }, [data, dataRange, props.useLoadConf]);

  const transformedData = useMemo((): {
    dataArray: number[];
    lastValueTime: null | Date;
  } => {
    if (!props.useLoadConf && !data?.[0]?.data?.length)
      return { dataArray: [], lastValueTime: null };
    if (props.useLoadConf && !dataRange?.[0]?.data?.length)
      return { dataArray: [], lastValueTime: null };

    const values = sortedData.map(d => d.value * valueScaling);
    const lastValueTime = new Date(sortedData[sortedData.length - 1].key);

    return { dataArray: values, lastValueTime };
  }, [data, dataRange, sortedData, valueScaling, props.useLoadConf]);

  const formattedData = useMemo(() => {
    return sortedData.map(d => ({ y: d.value * valueScaling, x: new Date(d.key) }));
  }, [sortedData, valueScaling]);

  if (isLoading || isLoadingRange) {
    return <ProgressSpinner />;
  }

  // props from legacy config
  const legacyProps = {
    // @ts-expect-error
    constant: props.constant,
    // @ts-expect-error
    dataset: props.baselineDataset,
    // @ts-expect-error
    usage: (props.useConstant ? "constant" : "dataset") as Usages,
    // @ts-expect-error
    color: props.baselineColor,
    // @ts-expect-error
    width: props.baselineWidth,
    // @ts-expect-error
    fillAboveColor: props.fillAboveColor,
    // @ts-expect-error
    fillBelowColor: props.fillBelowColor,
  };

  const advancedProps: AdvancedProps = {
    baseline: {
      ...legacyProps,
      ...props.baseline,
    },
    data: formattedData,
    minLine: props.minLine,
    maxLine: props.maxLine,
    count,
    updateFrequency: props.updateFrequency,
    referenceTime: props.referenceTime,
    showYAxis: props.showYAxis,
  };

  const handleSparkLineTitle = () => {
    if (props.useLoadConf) {
      return dataRange?.[0].title ?? "";
    }
    return data?.[0].title ?? "";
  };

  return (!props.useLoadConf && data?.length) || (props.useLoadConf && dataRange?.length) ? (
    <SparkLineHandler
      dataArray={transformedData.dataArray}
      chartIndex={props.chartIndex}
      lastValueTime={transformedData.lastValueTime}
      decimalPlaces={props.decimalPlaces}
      labelFontSize={props.labelFontSize}
      sparklineWidth={props.sparkLineWidth}
      lineColor={props.lineColor}
      lineWidth={props.lineWidth}
      unit={props.unit}
      maxAge={props.maxAge}
      dataset={dataset}
      title={handleSparkLineTitle()}
      height={props.height}
      overlayLabel={props.overlayLabel}
      vertical={props.vertical}
      horizontal={props.horizontal}
      advanced={props.advanced}
      advancedProps={advancedProps}
      generalProperties={props.generalProperties}
      isMultipleCharts={props.isMultipleCharts}
      currentBiggestValueWidth={props.currentBiggestValueWidth}
      setCurrentBiggestValueWidth={props.setCurrentBiggestValueWidth}
      stringValueMapper={data?.[0].stringValueMapper}
      displayCharts={props.displayCharts}
      minYAxis={props.minYAxis}
      maxYAxis={props.maxYAxis}
    />
  ) : (
    <div style={{ height: `${props.height ?? "auto"}px` }}>{T("word.no-records")}</div>
  );
}
